import { type ComponentType, type FC, lazy, Suspense } from "react";

/**
 * Route-based code splitting for AfriFinder.
 *
 * The page registry (pages/index.js → pageSetToRoutes) reads each page's
 * static route metadata (path / exact / title) SYNCHRONOUSLY at module
 * load to build the route table. A raw React.lazy() component hides those
 * props inside the async module, so it can't be registered directly.
 *
 * lazyFinderPage() bridges that: it returns a tiny wrapper component that
 * carries the metadata statically (declared here) while its heavy body is
 * imported on demand behind <Suspense>. Every Finder page shares ONE
 * "finder" webpack chunk, so:
 *   - Label-tool users never download Finder page code in main.js, and
 *   - the first /finder visit loads the Finder sub-app once; subsequent
 *     Finder navigation needs no further chunk fetches.
 */

type PageMeta = { path: string; exact: boolean; title: string };
type LazyLoader = () => Promise<{ default: ComponentType<any> }>;

// Shown while the shared Finder chunk loads on first entry. Inline styles
// only — the Finder stylesheet ships INSIDE the chunk we're awaiting, so
// its classes aren't available yet.
const ChunkFallback: FC = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#93897a",
      font: "500 15px/1.4 system-ui, -apple-system, sans-serif",
    }}
  >
    Loading…
  </div>
);

function lazyFinderPage(loader: LazyLoader, meta: PageMeta): ComponentType<any> {
  const Lazy = lazy(loader);
  const Page: FC<any> = (props) => (
    <Suspense fallback={<ChunkFallback />}>
      <Lazy {...props} />
    </Suspense>
  );
  Page.displayName = `LazyFinderPage(${meta.path})`;
  (Page as unknown as PageMeta).path = meta.path;
  (Page as unknown as PageMeta).exact = meta.exact;
  (Page as unknown as PageMeta).title = meta.title;
  return Page;
}

// Order matches the old eager registry: the more specific routes must come
// BEFORE the catch-all "/finder" discover route so react-router matches
// the narrower path first.
export const FINDER_PAGES: ComponentType<any>[] = [
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderDocsPage").then((m) => ({
        default: m.FinderDocsPage,
      })),
    { path: "/finder/docs", exact: false, title: "Handbook" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderApplyPage").then((m) => ({
        default: m.FinderApplyPage,
      })),
    { path: "/finder/apply", exact: true, title: "Apply to AfriFinder" },
  ),
  // The detail route must come BEFORE the index: "/finder/communities"
  // is a prefix of every slug under it, and react-router takes the
  // first match.
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderCommunityPage").then((m) => ({
        default: m.FinderCommunityPage,
      })),
    { path: "/finder/communities/:slug", exact: true, title: "Community" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderCommunitiesPage").then((m) => ({
        default: m.FinderCommunitiesPage,
      })),
    { path: "/finder/communities", exact: true, title: "Communities" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderAdminApplicationsPage").then((m) => ({
        default: m.FinderAdminApplicationsPage,
      })),
    { path: "/finder/admin", exact: false, title: "Review" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderMyProfilePage").then((m) => ({
        default: m.FinderMyProfilePage,
      })),
    { path: "/finder/me", exact: false, title: "My Finder profile" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderPublicProfilePage").then((m) => ({
        default: m.FinderPublicProfilePage,
      })),
    { path: "/finder/u/:handle", exact: true, title: "Profile" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderVerifyEmailPage").then((m) => ({
        default: m.FinderVerifyEmailPage,
      })),
    { path: "/finder/verify/institutional-email", exact: true, title: "Verify email" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderAskComposePage").then((m) => ({
        default: m.FinderAskComposePage,
      })),
    { path: "/finder/asks/new", exact: true, title: "New ask" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderAskComposePage").then((m) => ({
        default: m.FinderAskComposePage,
      })),
    { path: "/finder/asks/:askId/edit", exact: true, title: "Edit ask" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderAskDetailPage").then((m) => ({
        default: m.FinderAskDetailPage,
      })),
    { path: "/finder/asks/:askId", exact: true, title: "Ask" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderAsksPage").then((m) => ({
        default: m.FinderAsksPage,
      })),
    { path: "/finder/asks", exact: true, title: "Asks" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderProjectsPage").then((m) => ({
        default: m.FinderProjectsPage,
      })),
    { path: "/finder/projects", exact: true, title: "Open for contributors" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderActivityPage").then((m) => ({
        default: m.FinderActivityPage,
      })),
    { path: "/finder/activity", exact: true, title: "Activity" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderRecruitPage").then((m) => ({
        default: m.FinderRecruitPage,
      })),
    { path: "/finder/recruit", exact: true, title: "Invite contributors" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderInvitesPage").then((m) => ({
        default: m.FinderInvitesPage,
      })),
    { path: "/finder/invites", exact: true, title: "Invitations" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderContributorsPage").then((m) => ({
        default: m.FinderContributorsPage,
      })),
    { path: "/finder/contributors", exact: true, title: "Your contributors" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderDiscoverPage").then((m) => ({
        default: m.FinderDiscoverPage,
      })),
    { path: "/finder/people", exact: true, title: "People" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderMessagesPage").then((m) => ({
        default: m.FinderMessagesPage,
      })),
    { path: "/finder/messages/:handle", exact: true, title: "Messages" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderMessagesPage").then((m) => ({
        default: m.FinderMessagesPage,
      })),
    { path: "/finder/messages", exact: true, title: "Messages" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderAnalyticsPage").then((m) => ({
        default: m.FinderAnalyticsPage,
      })),
    { path: "/finder/analytics", exact: true, title: "Your analytics" },
  ),
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderSettingsPage").then((m) => ({
        default: m.FinderSettingsPage,
      })),
    { path: "/finder/settings", exact: false, title: "Settings" },
  ),
  // Home feed LAST so every more-specific /finder/* route above wins.
  lazyFinderPage(
    () =>
      import(/* webpackChunkName: "finder" */ "./FinderHomePage").then((m) => ({
        default: m.FinderHomePage,
      })),
    { path: "/finder", exact: true, title: "Home" },
  ),
];
